/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';

import {
  ArrowRightOutlined,
  BankOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  LoadingOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Button, Modal, Radio, Tooltip } from 'antd';
import { useDispatch } from 'react-redux';

import {
  AccountActions,
  AuthActions,
  UserActions,
  UserImpersonationActions,
} from '@portal/actions';
import Logo from '@portal/assets/svg/logos/login_logo.svg?react';
import {
  UserImpersonationStatus,
  getUserImpersonationStatus,
  getUserImpersonationStatusColor,
} from '@portal/enum/userImpersonationStatus';
import useReduxState from '@portal/hooks/useReduxState';
import { translate } from '@portal/services/i18n';
import { maskCpfOrCnpjHide } from '@portal/services/masks';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { multiUser } from '@portal/config/app.json';
import { getStackPath } from '@portal/config/routes';
import { useNavigation } from '@portal/hooks/useNavigation';
import * as StorageService from '@portal/services/storage';
import * as Sentry from '@sentry/react';

const COLORS = {
  SUCCESS: '#2ecc71',
  GRAY_TEXT: '#919191',
  GRAY_500: '#8c8c8c',
  WARNING: '#f39c12',
  DANGER: '#e74c3c',
} as const;

interface AccountOption {
  id: string;
  name: string;
  accountNumber: string;
  taxIdentifier: string;
  status: UserImpersonationStatus;
  isCurrentAccount: boolean;
  impersonationId?: string;
}

const MultiAccessSelection = () => {
  const navigate = useNavigation();
  const dispatch = useDispatch();

  if (multiUser.hideInviteAndAccountSelect) {
    navigate(getStackPath('HOME'));
    return null;
  }

  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAccounts, setFilteredAccounts] = useState<AccountOption[]>([]);
  const [isSwitching, setIsSwitching] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    type: 'accept' | 'reject' | null;
    account: AccountOption | null;
  }>({
    visible: false,
    type: null,
    account: null,
  });
  const MAX_VISIBLE_ACCOUNTS = 3;

  const {
    userImpersonation: userImpersonationData,
    loading,
    user: { me: loggedUser },
    theme: { currentTheme },
  } = useReduxState();

  const transformAccountData = (
    apiResponse: models.UserImpersonation
  ): AccountOption[] => {
    const accounts: AccountOption[] = [];

    if (loggedUser && loggedUser.id) {
      accounts.push({
        id: loggedUser.id,
        name: (loggedUser.name || loggedUser.account?.name) ?? '',
        accountNumber: loggedUser.name || loggedUser.account?.name || 'N/A',
        taxIdentifier: loggedUser.account?.taxIdentifier || 'N/A',
        status: UserImpersonationStatus.ACCEPTED,
        isCurrentAccount: true,
      });
    }

    if (
      apiResponse &&
      apiResponse.received &&
      Array.isArray(apiResponse.received)
    ) {
      const impersonationAccounts = apiResponse.received.map(
        (item: models.UserImpersonation['received'][0]) => ({
          id: item.targetUserId,
          name: item.targetUser?.name || 'N/A',
          accountNumber: item.targetUser?.login || 'N/A',
          taxIdentifier: item.targetUser?.taxIdentifier || 'N/A',
          status: item.status as UserImpersonationStatus,
          isCurrentAccount: false,
          impersonationId: item.id,
        })
      );
      accounts.push(...impersonationAccounts);
    }

    return accounts;
  };

  const getActiveAccounts = (accounts: AccountOption[]) => {
    return accounts.filter(
      (account) => account.status === UserImpersonationStatus.ACCEPTED
    );
  };

  const getPendingAccounts = (accounts: AccountOption[]) => {
    return accounts.filter(
      (account) => account.status === UserImpersonationStatus.PENDING
    );
  };

  const handleAccountSelect = (accountId: string) => {
    setSelectedAccount(accountId);
  };

  const handleEnter = async () => {
    if (selectedAccount && !isSwitching) {
      const account = filteredAccounts.find(
        (acc) => acc.id === selectedAccount
      );
      if (account) {
        setIsSwitching(true);
        try {
          if (account.isCurrentAccount) {
            window.location.href = getStackPath('HOME');
          } else {
            await dispatch(AccountActions.switchAccount(selectedAccount));
          }
        } catch (error) {
          Sentry.captureException(error, {
            extra: {
              context: 'MultiAccessSelection.handleEnter',
              selectedAccount,
            },
          });
          setIsSwitching(false);
        }
      }
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleRefresh = () => {
    dispatch(UserImpersonationActions.getUserImpersonationList());
  };

  const handleAcceptRequest = async () => {
    if (confirmModal.account?.impersonationId) {
      try {
        await dispatch(
          UserImpersonationActions.acceptUserImpersonation(
            confirmModal.account.impersonationId
          )
        );
        setConfirmModal({ visible: false, type: null, account: null });
        setSelectedAccount('');
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            context: 'MultiAccessSelection.handleAcceptRequest',
            impersonationId: confirmModal.account.impersonationId,
          },
        });
      }
    }
  };

  const handleRejectRequest = async () => {
    if (confirmModal.account?.impersonationId) {
      try {
        await dispatch(
          UserImpersonationActions.rejectUserImpersonation(
            confirmModal.account.impersonationId
          )
        );
        setConfirmModal({ visible: false, type: null, account: null });
        setSelectedAccount('');
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            context: 'MultiAccessSelection.handleRejectRequest',
            impersonationId: confirmModal.account.impersonationId,
          },
        });
      }
    }
  };

  const showConfirmModal = (
    type: 'accept' | 'reject',
    account: AccountOption
  ) => {
    setConfirmModal({ visible: true, type, account });
  };

  const getStatusColor = (status: string) => {
    return getUserImpersonationStatusColor(status);
  };

  const getStatusText = (status: string) => {
    return getUserImpersonationStatus(status);
  };

  useEffect(() => {
    if (
      !userImpersonationData?.received ||
      userImpersonationData?.received.length === 0
    ) {
      dispatch(UserImpersonationActions.getUserImpersonationList());
    }

    if (!loggedUser || !loggedUser.id) {
      dispatch(UserActions.getMe());
    }

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && selectedAccount && !isSwitching) {
        handleEnter();
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      setIsSwitching(false);
      document.removeEventListener('keydown', handleKeyPress);

      const sessionToken = StorageService.getItem('session-token');
      if (sessionToken) {
        try {
          const tokenData = JSON.parse(sessionToken);
          if (tokenData.accessToken === null && tokenData.originalAccessToken) {
            const restoredTokenData = {
              ...tokenData,
              accessToken: tokenData.originalAccessToken,
            };
            delete restoredTokenData.originalAccessToken;
            StorageService.setItem(
              'session-token',
              JSON.stringify(restoredTokenData)
            );
          }
        } catch (error) {
          Sentry.captureException(error, {
            extra: { context: 'MultiAccessSelection.restoreAccessToken' },
          });
        }
      }
    };
  }, [dispatch, loggedUser, selectedAccount, isSwitching]);

  useEffect(() => {
    if (loggedUser && loggedUser.id) {
      const transformedAccounts = transformAccountData(userImpersonationData);
      const activeAccounts = getActiveAccounts(transformedAccounts);

      if (activeAccounts.length === 0 && loading.amount === 0 && !isSwitching) {
        const timeoutId = setTimeout(() => {
          navigate(getStackPath('HOME'));
        }, 1000);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [
    userImpersonationData,
    loggedUser,
    navigate,
    loading.amount,
    isSwitching,
  ]);

  useEffect(() => {
    if (
      activeTab === 'active' &&
      filteredAccounts.length > 0 &&
      !selectedAccount
    ) {
      setSelectedAccount(filteredAccounts[0].id);
    }
  }, [activeTab, filteredAccounts, selectedAccount]);

  useEffect(() => {
    if (loggedUser && loggedUser.id) {
      const transformedAccounts = transformAccountData(userImpersonationData);
      const accountsToFilter =
        activeTab === 'active'
          ? getActiveAccounts(transformedAccounts)
          : getPendingAccounts(transformedAccounts);

      const filtered = accountsToFilter.filter(
        (account) =>
          account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          account.accountNumber.includes(searchTerm) ||
          account.taxIdentifier.includes(searchTerm)
      );
      setFilteredAccounts(filtered);

      if (activeTab === 'active' && filtered.length > 0 && !selectedAccount) {
        setSelectedAccount(filtered[0].id);
      }
    }
  }, [
    searchTerm,
    userImpersonationData,
    loggedUser,
    activeTab,
    selectedAccount,
  ]);

  const renderAccountItem = (account: AccountOption) => (
    <div
      className={`multi-access-selection__box__card__tabs__list__item ${
        selectedAccount === account.id ? 'selected' : ''
      } ${account.isCurrentAccount ? 'current' : ''}`}
      onClick={() => handleAccountSelect(account.id)}
      key={account.id}
    >
      <div className="multi-access-selection__box__card__tabs__list__item__status">
        <ThemedText
          className="multi-access-selection__account-status__text"
          style={{ color: getStatusColor(account.status) }}
        >
          {getStatusText(account.status)}
        </ThemedText>
      </div>
      <div className="multi-access-selection__box__card__tabs__list__item__info">
        <div
          className="multi-access-selection__box__card__tabs__list__item__icon"
          style={{ background: currentTheme.background }}
        >
          <BankOutlined />
        </div>
        <div>
          <ThemedText
            variant="body"
            className="multi-access-selection__box__card__tabs__list__item__info__name"
          >
            {account.name}
          </ThemedText>
          <div className="multi-access-selection__box__card__tabs__list__item__info__row">
            <ThemedText className="multi-access-selection__account-field__label">
              {translate(
                'PAGES.AUTH.MULTI_ACCESS_SELECTION.ACCOUNT_FIELDS.TAX_IDENTIFIER'
              )}
            </ThemedText>
            <ThemedText variant="body">
              {maskCpfOrCnpjHide(account.taxIdentifier)}
            </ThemedText>
          </div>
        </div>
      </div>
      <div>
        {account.status === UserImpersonationStatus.PENDING ? (
          <div className="muti-access-selection__pending">
            <Button
              type="text"
              icon={<CheckOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                showConfirmModal('accept', account);
              }}
              style={{ color: COLORS.SUCCESS }}
              title={translate(
                'PAGES.AUTH.MULTI_ACCESS_SELECTION.ACCEPT_BUTTON'
              )}
            />
            <Button
              type="text"
              icon={<CloseCircleOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                showConfirmModal('reject', account);
              }}
              style={{ color: COLORS.DANGER }}
              title={translate(
                'PAGES.AUTH.MULTI_ACCESS_SELECTION.REJECT_BUTTON'
              )}
            />
          </div>
        ) : (
          <Radio
            checked={selectedAccount === account.id}
            onChange={() => handleAccountSelect(account.id)}
          />
        )}
      </div>
    </div>
  );

  const transformedAccounts = transformAccountData(userImpersonationData);
  const activeAccounts = getActiveAccounts(transformedAccounts);
  const pendingAccounts = getPendingAccounts(transformedAccounts);

  const tabOptions = [
    {
      label: `${translate('PAGES.AUTH.MULTI_ACCESS_SELECTION.TABS.ACTIVE')} (${
        activeAccounts.length
      })`,
      key: 'active',
    },
    {
      label: `${translate('PAGES.AUTH.MULTI_ACCESS_SELECTION.TABS.PENDING')} (${
        pendingAccounts.length
      })`,
      key: 'pending',
    },
  ];

  const tabKeyChildren = [
    {
      key: 'active',
      children: (
        <div className="multi-access-selection__box__card__tabs__list">
          {loading.amount > 0 || !loggedUser || !loggedUser.id ? (
            <div className="multi-access-selection__loading">
              <ThemedText variant="body">
                {translate(
                  'PAGES.AUTH.MULTI_ACCESS_SELECTION.LOADING.ACCOUNTS'
                )}
              </ThemedText>
            </div>
          ) : filteredAccounts.length === 0 ? (
            <div className="multi-access-selection__empty">
              <ThemedText variant="body">
                {searchTerm
                  ? translate(
                      'PAGES.AUTH.MULTI_ACCESS_SELECTION.EMPTY_STATES.NO_ACCOUNTS_FOUND'
                    )
                  : translate(
                      'PAGES.AUTH.MULTI_ACCESS_SELECTION.EMPTY_STATES.NO_ACTIVE_ACCOUNTS'
                    )}
              </ThemedText>
            </div>
          ) : (
            filteredAccounts
              .slice(0, MAX_VISIBLE_ACCOUNTS)
              .map(renderAccountItem)
          )}
        </div>
      ),
    },
    {
      key: 'pending',
      children: (
        <div className="multi-access-selection__accounts">
          {loading.amount > 0 || !loggedUser || !loggedUser.id ? (
            <div className="multi-access-selection__loading">
              <ThemedText>
                {translate(
                  'PAGES.AUTH.MULTI_ACCESS_SELECTION.LOADING.REQUESTS'
                )}
              </ThemedText>
            </div>
          ) : filteredAccounts.length === 0 ? (
            <div className="multi-access-selection__empty">
              <ThemedText>
                {searchTerm
                  ? translate(
                      'PAGES.AUTH.MULTI_ACCESS_SELECTION.EMPTY_STATES.NO_REQUESTS_FOUND'
                    )
                  : translate(
                      'PAGES.AUTH.MULTI_ACCESS_SELECTION.EMPTY_STATES.NO_PENDING_REQUESTS'
                    )}
              </ThemedText>
            </div>
          ) : (
            filteredAccounts.map(renderAccountItem)
          )}
        </div>
      ),
    },
  ];

  return (
    <div
      className="multi-access-selection"
      style={{ background: currentTheme.background }}
    >
      <div className="multi-access-selection__header">
        <div className="multi-access-selection__box">
          <div className="multi-access-selection__box__card">
            <div className="multi-access-selection__header__logo">
              <Logo />
            </div>
            <div>
              <ThemedText
                variant="body"
                className="multi-access-selection__box__card__title"
              >
                {translate('PAGES.AUTH.MULTI_ACCESS_SELECTION.TITLE')}
              </ThemedText>
              <ThemedText
                variant="body"
                className="multi-access-selection__box__card__subtitle"
              >
                {translate('PAGES.AUTH.MULTI_ACCESS_SELECTION.SUBTITLE')}
              </ThemedText>
            </div>
            <div className="multi-access-selection__box__card__search">
              <AdvancedInput
                label={translate(
                  'PAGES.AUTH.MULTI_ACCESS_SELECTION.SEARCH_LABEL'
                )}
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder={translate(
                  'PAGES.AUTH.MULTI_ACCESS_SELECTION.SEARCH_PLACEHOLDER'
                )}
                endIcon={
                  searchTerm ? (
                    <CloseOutlined
                      onClick={clearSearch}
                      style={{ cursor: 'pointer', color: COLORS.GRAY_TEXT }}
                    />
                  ) : null
                }
              />
              <Tooltip
                title={translate(
                  'PAGES.AUTH.MULTI_ACCESS_SELECTION.REFRESH_TOOLTIP'
                )}
              >
                <ReloadOutlined
                  onClick={handleRefresh}
                  className="multi-access-selection__reload"
                />
              </Tooltip>
            </div>
            <div className="multi-access-selection__box__card__tabs">
              {tabOptions.map((tab) => (
                <button
                  key={tab.key}
                  className={`multi-access-selection__box__card__tabs__button ${
                    activeTab === tab.key ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    background:
                      activeTab === tab.key
                        ? currentTheme.background
                        : 'transparent',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {tabKeyChildren.map(
              (tab) =>
                tab.key === activeTab && <div key={tab.key}>{tab.children}</div>
            )}
            {activeTab === 'active' && filteredAccounts.length > 0 && (
              <div className="multi-access-selection__button">
                <AdvancedButton
                  text={translate(
                    'PAGES.AUTH.MULTI_ACCESS_SELECTION.ENTER_BUTTON'
                  )}
                  onClick={handleEnter}
                  disabled={!selectedAccount || isSwitching}
                  loading={isSwitching}
                  fullWidth
                  endIcon={
                    isSwitching ? <LoadingOutlined /> : <ArrowRightOutlined />
                  }
                />
              </div>
            )}
            <ThemedText variant="body" className="multi-access-selection__help">
              {translate('PAGES.AUTH.MULTI_ACCESS_SELECTION.HELP_TEXT')}
            </ThemedText>
            <ThemedText
              variant="body"
              className="multi-access-selection__logout"
              underline
              onClick={() => dispatch(AuthActions.logout())}
            >
              {translate('PAGES.AUTH.MULTI_ACCESS_SELECTION.LOGOUT')}
            </ThemedText>
          </div>
        </div>
      </div>
      <Modal
        title={
          confirmModal.type === 'accept'
            ? translate(
                'PAGES.AUTH.MULTI_ACCESS_SELECTION.CONFIRM_ACCEPT_TITLE'
              )
            : translate(
                'PAGES.AUTH.MULTI_ACCESS_SELECTION.CONFIRM_REJECT_TITLE'
              )
        }
        open={confirmModal.visible}
        onOk={
          confirmModal.type === 'accept'
            ? handleAcceptRequest
            : handleRejectRequest
        }
        onCancel={() =>
          setConfirmModal({ visible: false, type: null, account: null })
        }
        okText={
          confirmModal.type === 'accept'
            ? translate('PAGES.AUTH.MULTI_ACCESS_SELECTION.ACCEPT_BUTTON')
            : translate('PAGES.AUTH.MULTI_ACCESS_SELECTION.REJECT_BUTTON')
        }
        cancelText={translate('PAGES.AUTH.MULTI_ACCESS_SELECTION.MODAL.CANCEL')}
        confirmLoading={loading.amount > 0}
      >
        <ThemedText>
          {confirmModal.type === 'accept'
            ? translate('PAGES.AUTH.MULTI_ACCESS_SELECTION.CONFIRM_ACCEPT')
            : translate('PAGES.AUTH.MULTI_ACCESS_SELECTION.CONFIRM_REJECT')}
        </ThemedText>
        {confirmModal.account ? (
          <div>
            <ThemedText variant="body">
              <strong>
                {translate('PAGES.AUTH.MULTI_ACCESS_SELECTION.MODAL.NAME')}
              </strong>{' '}
              {confirmModal.account.name}
            </ThemedText>
            <ThemedText variant="body">
              <strong>
                {translate(
                  'PAGES.AUTH.MULTI_ACCESS_SELECTION.MODAL.TAX_IDENTIFIER'
                )}
              </strong>{' '}
              {maskCpfOrCnpjHide(confirmModal.account.taxIdentifier)}
            </ThemedText>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default MultiAccessSelection;
